// Any card of the staff member's reach (/admin/cardholders/{cardholderId}/cards/{cardId}/*), in the shapes the legacy
// console pages read (CardAccount, Transaction, CardLimits, ActionResult). A card is named "cardholderId:cardId": that
// is the "reference" the pages pass around.
import { amount, api, BffError, newIdempotencyKey, pascal, seg } from './bff';

/** CardAccountResponse */
export interface CardAccount {
  isConnected: boolean;
  message: string;
  displayName: string | null;
  email: string | null;
  availableBalance: number | null;
  ledgerBalance: number | null;
  currency: string;
  cardLastFour: string | null;
  cardStatus: string | null;
  cardReferenceId: string | null;
  /** What this card's processor offers beyond the common actions (unload, close…); all false when unknown. */
  capabilities: CardCapabilities;
  /** A kiosk batch card: in stock or sold, its card reference number's last four, QBalance on or off. */
  kioskCard?: KioskCard | null;
}

export interface KioskCard {
  batchId: string;
  stock: 'in_stock' | 'sold' | null;
  cardReferenceLast4: string | null;
  webAccess: boolean;
}

/** The API's `capabilities` of a card (docs/PARTNER_API.md § 4.3). */
export interface CardCapabilities {
  ssnRequired: boolean;
  kycQuestions: boolean;
  unload: boolean;
  reverse: boolean;
  closeCard: boolean;
  pinWidget: boolean;
  cardImage: boolean;
}

export const NO_CAPABILITIES: CardCapabilities = {
  ssnRequired: false,
  kycQuestions: false,
  unload: false,
  reverse: false,
  closeCard: false,
  pinWidget: false,
  cardImage: false,
};

/**
 * The line that names the card: its last four digits ("•••• 5104"). InterCash gives those once, when a card is issued,
 * and never for a replacement card; a card without them is named by InterCash's own identifier for it (the SAN, which
 * is not the number on the card and cannot pay for anything), its last four digits only: "Card ID •••• 8824". W26.
 */
export function cardNumberLine(
  account: Pick<CardAccount, 'cardLastFour' | 'cardReferenceId'>,
  provider: string | null | undefined,
): { text: string; isReference: boolean } {
  if (account.cardLastFour) return { text: `\u2022\u2022\u2022\u2022 ${account.cardLastFour}`, isReference: false };
  if (provider === 'InterCash' && account.cardReferenceId)
    return { text: `Card ID \u2022\u2022\u2022\u2022 ${account.cardReferenceId.slice(-4)}`, isReference: true };
  return { text: '\u2022\u2022\u2022\u2022 ----', isReference: false };
}

/** GET …/limits (D35): what the card may still be loaded with today; `supported` only for InterCash. */
export interface CardLimits {
  supported: boolean;
  message: string | null;
  currency: string | null;
  availableBalance: number | null;
  amountLeftToLoad: number | null;
  dailyLoadLimit: number | null;
  dailyLoadFrequencyLimit: number | null;
  loadsLeft: number | null;
}

/** ProviderTransactionSummary */
export interface Transaction {
  providerTransactionReference: string | null;
  transactionType: string;
  description: string;
  amount: number;
  currency: string;
  status: string;
  postedAt: string | null;
}

/** ProfileResponse (the address only on reads) */
export interface Profile {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateOrProvince?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

/** ProviderActionResult */
export interface ActionResult {
  success: boolean;
  message: string;
  providerReference: string | null;
}

interface LiveCard {
  id: string;
  last4: string | null;
  status: string;
  currency: string;
  availableBalance: string | null;
  ledgerBalance: string | null;
  stale: boolean;
  capabilities?: CardCapabilities;
  kioskCard?: KioskCard | null;
}

interface ApiTransaction {
  reference: string | null;
  type: string;
  description: string;
  amount: string;
  currency: string;
  status: string;
  postedAt: string | null;
}

/** A read that fails is "nothing" (null / no rows), as the legacy pages saw it. */
async function orNull<T>(call: Promise<T>): Promise<T | null> {
  try {
    return await call;
  } catch (err) {
    if (err instanceof BffError) return null;
    throw err;
  }
}

/** A refused action carries the API's own words ("This card is closed."). */
export async function action<T extends { success: boolean; message: string }>(call: Promise<T>): Promise<T> {
  try {
    return (await call) ?? ({ success: false, message: 'No response from API.' } as T);
  } catch (err) {
    if (err instanceof BffError) return { success: false, message: err.message } as T;
    throw err;
  }
}

export const toCardAccount = (
  c: LiveCard,
  holder: { displayName: string | null; email: string | null },
): CardAccount => ({
  isConnected: true,
  message: c.stale ? 'Could not reach the card processor; this is the last known balance.' : '',
  displayName: holder.displayName,
  email: holder.email,
  availableBalance: amount(c.availableBalance),
  ledgerBalance: amount(c.ledgerBalance),
  currency: c.currency,
  cardLastFour: c.last4,
  // the console reads a locked card as "Suspended" (the processors' word); the API says "frozen" after a lock
  cardStatus: c.status === 'frozen' ? 'Suspended' : pascal(c.status),
  cardReferenceId: c.id,
  capabilities: { ...NO_CAPABILITIES, ...c.capabilities },
  kioskCard: c.kioskCard ?? null,
});

export const toTransaction = (t: ApiTransaction): Transaction => ({
  providerTransactionReference: t.reference,
  transactionType: pascal(t.type) ?? 'Unknown',
  description: t.description,
  amount: Number(t.amount),
  currency: t.currency,
  status: t.status,
  postedAt: t.postedAt,
});

/** "cardholderId:cardId" -> the card's path in the API. */
const card = (ref: string, rest = '') => {
  const [cardholderId = '', cardId = ''] = ref.split(':');
  return `/admin/cardholders/${seg(cardholderId)}/cards/${seg(cardId)}${rest}`;
};
const holderOf = (ref: string) => `/admin/cardholders/${seg(ref.split(':')[0] ?? '')}`;

interface Holder {
  name: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
}

interface Load {
  status: 'pending_approval' | 'pending' | 'submitted' | 'succeeded' | 'failed' | 'unknown' | 'rejected';
  amount: string;
  currency: string;
  message: string | null;
}

/** A load or an unload as the page tells it: done (empty message), waiting, refused, or no answer. */
function moneyOutcome(load: Load, word: 'load' | 'unload'): ActionResult {
  switch (load.status) {
    case 'succeeded':
      return { success: true, message: '', providerReference: null };
    case 'pending_approval':
      return {
        success: true,
        message: `This ${word} is above the approval threshold: it waits for another admin to approve it under Loads.`,
        providerReference: null,
      };
    case 'failed':
      return { success: false, message: `The processor refused: ${load.message ?? ''}`, providerReference: null };
    default:
      return {
        success: false,
        message: `The processor did not answer. Do NOT ${word} again: check the outcome under Loads first.`,
        providerReference: null,
      };
  }
}

export const cardApi = {
  account: (ref: string, _cardReferenceId?: string | null) =>
    orNull(
      Promise.all([api<LiveCard>('GET', card(ref)), api<Holder>('GET', holderOf(ref))]).then(([c, h]) =>
        toCardAccount(c, { displayName: h.name, email: h.email }),
      ),
    ),
  transactions: async (ref: string, _cardReferenceId: string) =>
    ((await orNull(api<ApiTransaction[]>('GET', card(ref, '/transactions')))) ?? []).map(toTransaction),
  profile: (ref: string) =>
    orNull(
      api<Holder>('GET', holderOf(ref)).then((h): Profile => ({
        firstName: h.firstName,
        lastName: h.lastName,
        email: h.email,
        phoneNumber: h.phone,
      })),
    ),
  /** InterCash's own enquiry of what the card may still be loaded with today (other processors have none). */
  limits: async (ref: string): Promise<CardLimits | null> => {
    try {
      const r = await api<{
        message: string | null;
        limits: {
          currency: string;
          availableBalance: string | null;
          amountLeftToLoad: string | null;
          dailyLoadLimit: string | null;
          dailyLoadFrequencyLimit: number | null;
          loadsLeft: number | null;
        } | null;
      }>('GET', card(ref, '/limits'));
      return {
        supported: true,
        message: r.message,
        currency: r.limits?.currency ?? null,
        availableBalance: amount(r.limits?.availableBalance),
        amountLeftToLoad: amount(r.limits?.amountLeftToLoad),
        dailyLoadLimit: amount(r.limits?.dailyLoadLimit),
        dailyLoadFrequencyLimit: r.limits?.dailyLoadFrequencyLimit ?? null,
        loadsLeft: r.limits?.loadsLeft ?? null,
      };
    } catch (err) {
      if (!(err instanceof BffError)) throw err;
      return err.code === 'CARD_ACTION_UNSUPPORTED'
        ? {
            supported: false,
            message: null,
            currency: null,
            availableBalance: null,
            amountLeftToLoad: null,
            dailyLoadLimit: null,
            dailyLoadFrequencyLimit: null,
            loadsLeft: null,
          }
        : null;
    }
  },
  updateProfile: (ref: string, body: Profile) =>
    action(
      api('PATCH', holderOf(ref), {
        body: { firstName: body.firstName, lastName: body.lastName, phone: body.phoneNumber || null },
      }).then((): ActionResult => ({ success: true, message: 'Saved.', providerReference: null })),
    ),
  /**
   * A load, with one Idempotency-Key (a retry of the same request never loads twice). What happened is in the
   * message: done (empty: the page says so), waiting for a second admin, refused, or no answer — never load again then.
   */
  load: (ref: string, body: { cardReferenceId: string; amount: number; currency: string }) =>
    action(
      api<Load>('POST', card(ref, '/loads'), {
        body: { amount: body.amount.toFixed(2), currency: body.currency },
        idempotencyKey: newIdempotencyKey(),
      }).then((load) => moneyOutcome(load, 'load')),
    ),
  /** Money taken back off the card (where the processor can): the same rules and words as a load. */
  unload: (ref: string, body: { amount: number; currency: string }) =>
    action(
      api<Load>('POST', card(ref, '/unloads'), {
        body: { amount: body.amount.toFixed(2), currency: body.currency },
        idempotencyKey: newIdempotencyKey(),
      }).then((load) => moneyOutcome(load, 'unload')),
    ),
  /** Close the card for good (where the processor offers it). */
  close: (ref: string, reason: CloseReason) =>
    action(
      api<{ message: string }>('POST', card(ref, '/close'), { body: { reason } }).then((r): ActionResult => ({
        success: true,
        message: r.message,
        providerReference: null,
      })),
    ),
  cardAction: (ref: string, name: CardAction, _userCardId: string) =>
    action(
      api<{ message: string }>('POST', card(ref, `/${name}`)).then((r): ActionResult => ({
        success: true,
        message: r.message,
        providerReference: null,
      })),
    ),
};

export type CardAction = 'activate' | 'freeze' | 'unfreeze' | 'reissue' | 'convert-to-physical';
export type CloseReason = 'lost' | 'stolen' | 'closed';
